import { RandomInt, secureRandomInt } from "../engine/cards.ts";
import { Action, GameEvent, PokerError, PokerTable, TableConfig } from "../engine/game.ts";
import { formatUSD, type Cents } from "../engine/money.ts";
import type { ClientMsg, PlayerView, ServerMsg, TableView } from "../shared/protocol.ts";
import { botDecide } from "./bots.ts";
import { safeAvatar } from "./discord.ts";

/** Time source, so tests can fast-forward and Cloudflare/Node/browser can all use the same room. */
export interface Scheduler {
  now(): number;
  setTimeout(fn: () => void, ms: number): unknown;
  clearTimeout(handle: unknown): void;
}

export const realScheduler: Scheduler = {
  now: () => Date.now(),
  setTimeout: (fn, ms) => setTimeout(fn, ms),
  clearTimeout: (h) => clearTimeout(h as ReturnType<typeof setTimeout>),
};

export interface RoomOptions {
  config?: Partial<TableConfig>;
  /** How long a player has to act before being auto-checked/folded. */
  turnMs?: number;
  /** Pause after a hand ends so everyone can watch the showdown. */
  nextHandMs?: number;
  /** Delay before the very first hand once two players are seated. */
  firstHandMs?: number;
  allowBots?: boolean;
  /** Skip the lobby: deal as soon as two people are seated. Handy for tests; real tables wait for the host. */
  autoStart?: boolean;
  rand?: RandomInt;
  scheduler?: Scheduler;
  /** Bot thinking time range in ms. */
  botThinkMs?: [number, number];
}

/** One connection (a browser tab / WebSocket). The room only needs `send`. */
export interface Conn {
  id: string;
  send(msg: ServerMsg): void;
}

interface Profile {
  name: string;
  avatar?: string;
  isBot: boolean;
  /** Consecutive turns that ran out of time. */
  timeouts: number;
}

const MAX_TIMEOUTS = 3;
/** The host can set the buy-in anywhere from 20 big blinds up to 10,000 big blinds (capped at $100,000). */
const MIN_BUYIN_BB = 20;
const MAX_BUYIN_BB = 10_000;
const MAX_BUYIN = 10_000_000;
/** Largest big blind the host can pick: $1,000. */
const MAX_BIG_BLIND = 100_000;

/**
 * One poker table. Holds the engine, the connections, the turn timer, the dealer's pacing and any bots.
 * It is plain TypeScript with no platform APIs so it runs unchanged inside a Cloudflare Durable Object,
 * in Node, or in the browser (for the offline demo).
 *
 * SECURITY: `join` trusts the playerId the client sends. In production the Worker must verify the
 * player's identity (Discord OAuth token) and pass the verified id in; see worker/index.ts.
 */
export class TableRoom {
  table: PokerTable;
  readonly allowBots: boolean;
  private readonly turnMs: number;
  private readonly nextHandMs: number;
  private readonly firstHandMs: number;
  private readonly botThinkMs: [number, number];
  private readonly rand: RandomInt;
  private readonly clock: Scheduler;

  private conns = new Map<string, Conn>();
  private playerOf = new Map<string, string>(); // connId -> playerId
  private profiles = new Map<string, Profile>(); // playerId -> profile

  private turnTimer: unknown = null;
  private turnDeadline = 0;
  private botTimer: unknown = null;
  private startTimer: unknown = null;
  private lastHandEndedAt: number | null = null;
  private botSeq = 0;
  private pendingEvents: GameEvent[] = [];
  /** Humans in the order they first joined; the first one still connected is the host. */
  private joinOrder: string[] = [];
  /** False while the host is setting up. The blinds lock when the host starts the game. */
  private started: boolean;
  private readonly baseConfig: Partial<TableConfig> | undefined;

  constructor(opts: RoomOptions = {}) {
    this.rand = opts.rand ?? secureRandomInt;
    this.clock = opts.scheduler ?? realScheduler;
    this.baseConfig = opts.config ? { ...opts.config } : undefined;
    this.table = new PokerTable(opts.config ? { ...opts.config } : undefined, this.rand);
    this.started = opts.autoStart ?? false;
    this.turnMs = opts.turnMs ?? 30_000;
    this.nextHandMs = opts.nextHandMs ?? 6_000;
    this.firstHandMs = opts.firstHandMs ?? 1_500;
    this.allowBots = opts.allowBots ?? false;
    this.botThinkMs = opts.botThinkMs ?? [900, 2200];
  }

  // ------------------------------------------------------------ connections

  connect(conn: Conn): void {
    this.conns.set(conn.id, conn);
  }

  disconnect(connId: string): void {
    const pid = this.playerOf.get(connId);
    this.conns.delete(connId);
    this.playerOf.delete(connId);
    // They keep their seat; the turn timer folds/checks for them and removes them if they stay gone.
    if (pid) this.broadcast([]);
  }

  receive(connId: string, msg: ClientMsg): void {
    const conn = this.conns.get(connId);
    if (!conn || typeof msg !== "object" || msg === null) return;
    try {
      this.handle(conn, msg);
    } catch (e) {
      if (e instanceof PokerError) conn.send({ t: "error", message: e.message });
      else throw e;
    }
  }

  // --------------------------------------------------------------- handling

  private handle(conn: Conn, msg: ClientMsg): void {
    if (msg.t === "join") {
      const id = String(msg.playerId ?? "").slice(0, 64);
      if (!id || id.startsWith("bot:")) throw new PokerError("Invalid player id");
      const name = String(msg.name ?? "").trim().slice(0, 24) || "Player";
      const avatar = safeAvatar(msg.avatar);
      this.playerOf.set(conn.id, id);
      if (!this.joinOrder.includes(id)) this.joinOrder.push(id);
      const prev = this.profiles.get(id);
      this.profiles.set(id, { name, avatar, isBot: false, timeouts: prev?.timeouts ?? 0 });
      const seated = this.table.findPlayer(id);
      if (seated) seated.name = name;
      conn.send({ t: "welcome", playerId: id, config: this.table.config, allowBots: this.allowBots });
      this.broadcast([]);
      this.maybeStartHand(); // someone came back to a table that was paused
      return;
    }

    const pid = this.playerOf.get(conn.id);
    if (!pid) throw new PokerError("Join the table first");

    switch (msg.t) {
      case "sit": {
        const profile = this.profiles.get(pid)!;
        const buyIn = this.cents(msg.buyIn);
        const seat = msg.seat === undefined ? undefined : Number(msg.seat);
        this.table.sitDown(pid, profile.name, buyIn, seat);
        this.afterChange();
        break;
      }
      case "act": {
        this.table.act(pid, this.parseAction(msg.action));
        this.profiles.get(pid)!.timeouts = 0;
        this.afterAction();
        break;
      }
      case "rebuy":
        this.table.rebuy(pid, this.cents(msg.amount));
        this.afterChange();
        break;
      case "setSettings":
        this.setSettings(pid, msg);
        break;
      case "startGame":
        this.startGame(pid);
        break;
      case "leave":
        if (this.table.findPlayer(pid)) {
          this.table.standUp(pid);
          this.afterAction();
        }
        break;
      case "addBot":
        if (!this.allowBots) throw new PokerError("Bots are disabled on this table");
        this.addBot();
        break;
      case "removeBots":
        if (!this.allowBots) throw new PokerError("Bots are disabled on this table");
        for (const p of [...this.table.players]) {
          if (p && this.profiles.get(p.id)?.isBot) {
            this.table.standUp(p.id);
            this.profiles.delete(p.id);
          }
        }
        this.afterAction();
        break;
      default:
        throw new PokerError("Unknown message");
    }
  }

  /** Who runs the table right now. */
  get hostId(): string | null {
    const online = new Set(this.playerOf.values());
    return this.joinOrder.find((id) => online.has(id)) ?? null;
  }

  /**
   * Host only. All three values are checked together and nothing changes unless all are valid.
   * The blinds can only be set BEFORE the game starts; once it has, they are locked for the whole game.
   * The buy-in can still be changed later: it only matters when someone sits down or rebuys.
   */
  private setSettings(by: string, msg: { smallBlind: unknown; bigBlind: unknown; buyIn: unknown }): void {
    if (by !== this.hostId) throw new PokerError("Only the host can change the table settings");
    const cfg = this.table.config;
    const small = this.cents(msg.smallBlind);
    const big = this.cents(msg.bigBlind);
    const buyIn = this.cents(msg.buyIn);
    for (const v of [small, big, buyIn]) {
      if (v % cfg.unit !== 0) throw new PokerError(`Amounts must be a multiple of ${formatUSD(cfg.unit)}`);
    }
    const blindsChanged = small !== cfg.smallBlind || big !== cfg.bigBlind;
    if (blindsChanged && this.started) throw new PokerError("The blinds are locked: they can't be changed once the game has started");
    if (big < small) throw new PokerError("The big blind can't be smaller than the small blind");
    if (big > MAX_BIG_BLIND) throw new PokerError(`The big blind can be at most ${formatUSD(MAX_BIG_BLIND)}`);
    const lo = big * MIN_BUYIN_BB;
    const hi = Math.min(big * MAX_BUYIN_BB, MAX_BUYIN);
    if (buyIn < lo || buyIn > hi) {
      throw new PokerError(`With a ${formatUSD(big)} big blind the buy-in must be between ${formatUSD(lo)} and ${formatUSD(hi)}`);
    }
    const buyInChanged = buyIn !== cfg.maxBuyIn;
    if (!blindsChanged && !buyInChanged) return;

    cfg.smallBlind = small;
    cfg.bigBlind = big;
    cfg.minBuyIn = buyIn; // one fixed buy-in for everyone
    cfg.maxBuyIn = buyIn;
    // Before the game starts nobody has played, so everyone already seated simply gets the new buy-in.
    if (!this.started) for (const p of this.table.players) if (p) p.stack = buyIn;
    this.broadcast([{ type: "settings_changed", buyIn, smallBlind: small, bigBlind: big, buyInChanged, blindsChanged }]);
  }

  /** Host only: lock in the stakes and begin. Hands deal on their own from here. */
  private startGame(by: string): void {
    if (by !== this.hostId) throw new PokerError("Only the host can start the game");
    if (this.started) throw new PokerError("The game has already started");
    const ready = this.table.players.filter((p) => p && p.stack > 0).length;
    if (ready < 2) throw new PokerError("Seat at least two players before starting");
    this.started = true;
    const c = this.table.config;
    this.broadcast([{ type: "game_started", smallBlind: c.smallBlind, bigBlind: c.bigBlind, buyIn: c.maxBuyIn }]);
    this.maybeStartHand();
  }

  /** When the last human has left, the game is over: back to a fresh lobby (blinds unlock for the next game). */
  private checkGameOver(): void {
    if (!this.started) return;
    // a player who stood up mid-hand lingers (folded) until the hand ends, but they are gone for this purpose
    const humanSeated = this.table.players.some((p) => p && !p.leaving && !this.profiles.get(p.id)?.isBot);
    if (humanSeated) return;
    for (const h of [this.turnTimer, this.botTimer, this.startTimer]) if (h !== null) this.clock.clearTimeout(h);
    this.turnTimer = this.botTimer = this.startTimer = null;
    this.turnDeadline = 0;
    for (const [id, prof] of [...this.profiles]) if (prof.isBot) this.profiles.delete(id);
    this.table = new PokerTable(this.baseConfig ? { ...this.baseConfig } : undefined, this.rand);
    this.started = false;
    this.lastHandEndedAt = null;
    this.botSeq = 0;
    this.pendingEvents.push({ type: "game_over" });
  }

  private cents(v: unknown): Cents {
    if (typeof v !== "number" || !Number.isInteger(v) || v <= 0 || v > 1e9) throw new PokerError("Bad amount");
    return v;
  }

  private parseAction(a: Action): Action {
    switch (a?.type) {
      case "fold": case "check": case "call": case "allin":
        return { type: a.type };
      case "raise":
        return { type: "raise", amount: this.cents(a.amount) };
      default:
        throw new PokerError("Unknown action");
    }
  }

  addBot(): void {
    if (this.table.players.every((p) => p !== null)) throw new PokerError("Table is full");
    const names = ["Vinnie", "Sal", "Moe", "Lefty", "Gino", "Paulie", "Tony", "Rocco"];
    const n = ++this.botSeq;
    const id = `bot:${n}`;
    const name = names[(n - 1) % names.length]!;
    this.profiles.set(id, { name, isBot: true, timeouts: 0 });
    this.table.sitDown(id, name, this.table.config.maxBuyIn);
    this.afterChange();
  }

  // ------------------------------------------------------- state and timers

  /** Something changed that doesn't move the action (join, sit, rebuy). */
  private afterChange(): void {
    this.broadcast(this.table.drainEvents());
    this.maybeStartHand();
  }

  /** Something changed that moves the action (act, leave, timeout, new hand). */
  private afterAction(): void {
    const events = this.table.drainEvents();
    if (events.some((e) => e.type === "hand_complete")) this.lastHandEndedAt = this.clock.now();
    this.checkGameOver();
    this.armTurn();
    this.broadcast([...events, ...this.pendingEvents.splice(0)]);
    this.maybeStartHand();
  }

  private armTurn(): void {
    if (this.turnTimer !== null) this.clock.clearTimeout(this.turnTimer);
    if (this.botTimer !== null) this.clock.clearTimeout(this.botTimer);
    this.turnTimer = this.botTimer = null;
    this.turnDeadline = 0;

    const seat = this.table.toAct;
    if (seat === null) return;
    const p = this.table.players[seat];
    if (!p) return;

    if (this.profiles.get(p.id)?.isBot) {
      const [lo, hi] = this.botThinkMs;
      const think = lo + this.rand(Math.max(1, hi - lo));
      this.botTimer = this.clock.setTimeout(() => this.botMove(seat, p.id), think);
    } else {
      this.turnDeadline = this.clock.now() + this.turnMs;
      this.turnTimer = this.clock.setTimeout(() => this.turnExpired(seat, p.id), this.turnMs);
    }
  }

  private botMove(seat: number, id: string): void {
    this.botTimer = null;
    if (this.table.toAct !== seat || this.table.players[seat]?.id !== id) return;
    this.table.act(id, botDecide(this.table, seat, this.rand));
    this.afterAction();
  }

  private turnExpired(seat: number, id: string): void {
    this.turnTimer = null;
    if (this.table.toAct !== seat || this.table.players[seat]?.id !== id) return;
    const profile = this.profiles.get(id);
    if (profile) profile.timeouts++;
    const legal = this.table.legalActions(id);
    this.table.act(id, legal?.check ? { type: "check" } : { type: "fold" });
    if (profile && profile.timeouts >= MAX_TIMEOUTS && this.table.findPlayer(id)) {
      this.table.standUp(id); // idle too long: give the seat up
      profile.timeouts = 0;
    }
    this.afterAction();
  }

  private maybeStartHand(): void {
    if (this.startTimer !== null || this.table.street !== "waiting" || !this.canDeal()) return;
    let delay = this.firstHandMs;
    if (this.lastHandEndedAt !== null) {
      delay = Math.max(this.firstHandMs, this.nextHandMs - (this.clock.now() - this.lastHandEndedAt));
    }
    this.startTimer = this.clock.setTimeout(() => {
      this.startTimer = null;
      for (const p of this.table.players) {
        // Bots reload for free; people choose to rebuy.
        if (p && p.stack === 0 && this.profiles.get(p.id)?.isBot) this.table.rebuy(p.id, this.table.config.maxBuyIn);
      }
      if (!this.canDeal()) return this.broadcast(this.table.drainEvents());
      this.table.startHand();
      this.afterAction();
    }, delay);
  }

  /** At least two seated players who either have chips or are bots that will reload. */
  private canDeal(): boolean {
    if (!this.started) return false; // the host hasn't started the game yet
    if (this.playerOf.size === 0) return false; // nobody watching: don't let bots play to an empty room
    const ready = this.table.players.filter((p) => p && (p.stack > 0 || this.profiles.get(p.id)?.isBot));
    return ready.length >= 2;
  }

  // ------------------------------------------------------------- broadcasting

  private broadcast(events: GameEvent[]): void {
    const turnMsLeft = this.turnDeadline ? Math.max(0, this.turnDeadline - this.clock.now()) : null;
    for (const conn of this.conns.values()) {
      const pid = this.playerOf.get(conn.id);
      if (!pid) continue; // not joined yet
      conn.send({ t: "update", view: this.viewFor(pid), config: this.table.config, events, turnMsLeft, turnMsTotal: this.turnMs });
    }
  }

  viewFor(playerId: string | null): TableView {
    const v = this.table.viewFor(playerId);
    const online = new Set(this.playerOf.values());
    const players = v.players.map((p): PlayerView | null => {
      if (!p) return null;
      const prof = this.profiles.get(p.id);
      return {
        ...p,
        avatar: prof?.avatar,
        isBot: prof?.isBot ?? false,
        connected: prof?.isBot ? true : online.has(p.id),
      };
    });
    return { ...v, players, legal: v.legal, hostId: this.hostId, started: this.started };
  }
}
